import { ShieldCheck, AlertTriangle, AlertOctagon, BadgeCheck, MapPinned, FileSignature } from "lucide-react";

// =============================================================================
// DocumentHealthPills
// =============================================================================
// Compact traffic-light strip showing the live expiry status of the 3 critical
// employee documents:
//   • Führerschein (driver_license_expiry)
//   • Identitätsdokument (Reisepass / Personalausweis → identity_doc_valid_until)
//   • Aufenthaltstitel (residence_valid_until) + Arbeitserlaubnis (work_permit_valid_until)
//     → only shown for Non-EU/EEA citizens
//
// Status thresholds (days until expiry):
//   • Expired              → red    (status="expired")
//   • <= 30 days remaining → red    (status="critical")
//   • <= 90 days remaining → amber  (status="warning")
//   • > 90 days remaining  → green  (status="ok")
//   • Missing date         → zinc   (status="missing")
// =============================================================================

const EU_EEA_PLUS_CH = new Set([
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT","LV","LT","LU","MT","NL",
  "PL","PT","RO","SK","SI","ES","SE",
  "IS","LI","NO",
  "CH",
]);
const needsResidenceDocs = (citizenship) => !!citizenship && !EU_EEA_PLUS_CH.has(citizenship);

function dayDiff(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date(); now.setHours(0,0,0,0);
  d.setHours(0,0,0,0);
  return Math.round((d - now) / 86400000);
}

function classifyDate(dateStr) {
  const days = dayDiff(dateStr);
  if (days === null) return { status: "missing", days: null };
  if (days < 0)  return { status: "expired",  days };
  if (days <= 30) return { status: "critical", days };
  if (days <= 90) return { status: "warning",  days };
  return { status: "ok", days };
}

// Combine two dates → take the worst status
function classifyPair(dateA, dateB) {
  const a = classifyDate(dateA);
  const b = classifyDate(dateB);
  const rank = { expired: 4, critical: 3, warning: 2, missing: 1, ok: 0 };
  return rank[a.status] >= rank[b.status] ? a : b;
}

const TONES = {
  expired:  { cls: "bg-red-500/15 text-red-300 border-red-500/40",         dot: "bg-red-400",       icon: AlertOctagon,  word: "abgelaufen" },
  critical: { cls: "bg-red-500/10 text-red-300 border-red-500/30",         dot: "bg-red-400",       icon: AlertOctagon,  word: "kritisch"   },
  warning:  { cls: "bg-amber-500/10 text-amber-300 border-amber-500/30",   dot: "bg-amber-400",     icon: AlertTriangle, word: "bald fällig"},
  ok:       { cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30", dot: "bg-emerald-400", icon: BadgeCheck,    word: "gültig"     },
  missing:  { cls: "bg-zinc-700/30 text-zinc-400 border-zinc-600/40",      dot: "bg-zinc-500",      icon: AlertTriangle, word: "fehlt"      },
};

function formatTooltip(label, dateStr, status, days) {
  const base = `${label}: `;
  if (status === "missing") return base + "kein Ablaufdatum hinterlegt";
  const d = dateStr ? new Date(dateStr).toLocaleDateString("de-AT") : "—";
  if (status === "expired")  return `${base}abgelaufen am ${d} (${Math.abs(days)} Tage überfällig)`;
  if (status === "ok")       return `${base}gültig bis ${d} (noch ${days} Tage)`;
  return `${base}gültig bis ${d} (nur noch ${days} Tage)`;
}

function Pill({ label, Icon, status, tooltip, testid }) {
  const tone = TONES[status];
  return (
    <span
      data-testid={testid}
      data-status={status}
      title={tooltip}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider border ${tone.cls} cursor-help`}
    >
      <Icon className="w-3 h-3" />
      <span>{label}</span>
      <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
    </span>
  );
}

export default function DocumentHealthPills({ employee }) {
  if (!employee) return null;

  const license = classifyDate(employee.driver_license_expiry);
  const identity = classifyDate(employee.identity_doc_valid_until);
  // Residence + Work-Permit combined into one pill — worst-of-two
  const residence = classifyPair(employee.residence_valid_until, employee.work_permit_valid_until);

  const showResidence = needsResidenceDocs(employee.citizenship);

  const idDocLabel = ({
    reisepass: "Reisepass",
    personalausweis: "Personalausweis",
    fuehrerschein_id: "Ausweis (FS)",
    andere: "Ausweis",
  }[employee.identity_doc_type] || "Ausweis");

  return (
    <div
      data-testid="emp-document-health-pills"
      className="flex flex-wrap items-center gap-2 mt-2.5"
      role="group"
      aria-label="Dokumenten-Gültigkeit"
    >
      <span className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mr-1">Dokumenten-Ampel:</span>
      <Pill
        label={`Führerschein · ${TONES[license.status].word}`}
        Icon={FileSignature}
        status={license.status}
        tooltip={formatTooltip("Führerschein", employee.driver_license_expiry, license.status, license.days)}
        testid="emp-doc-pill-license"
      />
      <Pill
        label={`${idDocLabel} · ${TONES[identity.status].word}`}
        Icon={ShieldCheck}
        status={identity.status}
        tooltip={formatTooltip(idDocLabel, employee.identity_doc_valid_until, identity.status, identity.days)}
        testid="emp-doc-pill-identity"
      />
      {showResidence && (
        <Pill
          label={`Aufenthaltstitel · ${TONES[residence.status].word}`}
          Icon={MapPinned}
          status={residence.status}
          tooltip={`Aufenthaltstitel: ${employee.residence_valid_until ? "gültig bis " + new Date(employee.residence_valid_until).toLocaleDateString("de-AT") : "kein Datum"} · Arbeitserlaubnis: ${employee.work_permit_valid_until ? "gültig bis " + new Date(employee.work_permit_valid_until).toLocaleDateString("de-AT") : "kein Datum"}`}
          testid="emp-doc-pill-residence"
        />
      )}
    </div>
  );
}
